import { CSOptionItem, DateRange, RelativeDate } from "../../types";
import { P, match } from "ts-pattern";
import { endOfDay, startOfDay, subDays } from "date-fns";

/**
 * List of relative date options as `{ id, label }` for easy use with dropdowns etc.
 */
export const relativeDateOptions: Readonly<CSOptionItem<RelativeDate>[]> = [
  { id: "all", label: "All" },
  { id: "30-days", label: "Last 30 Days" },
  { id: "60-days", label: "Last 60 Days" },
  { id: "90-days", label: "Last 90 Days" },
  { id: "180-days", label: "Last 180 Days" },
  { id: "365-days", label: "Last 365 Days" },
];

/**
 * Parse a string to a RelativeDate.
 * Useful, for example, for ensuring a query param is actually a valid RelativeDate.
 */
export const parseRelativeDate = (
  date: string | null | undefined,
  fallback: RelativeDate = "all",
): RelativeDate => {
  const option = relativeDateOptions.find((x) => x.id === date);
  return option?.id ?? fallback;
};

/**
 * Helper to build a date range based on how many days in the past.
 *
 * Note that the end date will be the end of **yesterday**.
 */
const getDaysAgoRange = (daysAgo: number): DateRange => {
  return {
    startDate: startOfDay(subDays(new Date(), daysAgo)),
    endDate: subDays(endOfDay(new Date()), 1),
  };
};

/**
 * Translates a relative date to a date range.
 */
export const relativeDateToDateRange = (date: RelativeDate): DateRange =>
  match(date)
    .with(P.union(P.nullish, "all"), () => ({
      startDate: null,
      endDate: null,
    }))
    .with("7-days", () => getDaysAgoRange(7))
    .with("30-days", () => getDaysAgoRange(30))
    .with("60-days", () => getDaysAgoRange(60))
    .with("90-days", () => getDaysAgoRange(90))
    .with("180-days", () => getDaysAgoRange(180))
    .with("365-days", () => getDaysAgoRange(365))
    .exhaustive();

const getDateDisplayString = (d: Date) => {
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * Returns a string representing the date range.
 */
export const getDateRangeAsString = (range: DateRange) => {
  if (range.startDate && range.endDate) {
    return `from ${getDateDisplayString(range.startDate)} to ${getDateDisplayString(range.endDate)}`;
  }

  if (range.startDate) {
    return `from ${getDateDisplayString(range.startDate)}`;
  }

  if (range.endDate) {
    return `through ${getDateDisplayString(range.endDate)}`;
  }

  return "for all dates";
};
